import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  AnyDynamicSchemaField,
  DynamicSchemaField,
  DynamicSchemaFieldKey,
  FileIdsValue,
  SelectMultipleValue,
} from '@kaizenplatform/hugot-entity';
import Immutable from 'immutable';

import {
  DynamicSchemaDefinition,
  DynamicSchemaDefinitionChoice,
  DynamicSchemaDefinitionFieldType,
  DynamicSchemaDefinitionFileIds,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionSection,
  DynamicSchemaDefinitionSelectOne,
  DynamicSchemaDefinitionSingleLineText,
} from '../../graphql/generated/graphql';
import FilesWebApi from '../../service/persistent/FilesWebApi';
import { ProjectType } from '../../service/persistent/ProjectWebApi';

export const jsonToDynamicSchemaValue = <T>(
  json?: any,
  definition?:
    | (Pick<DynamicSchemaDefinition, 'id'> & {
        sections: Array<
          Pick<DynamicSchemaDefinitionSection, 'id' | 'label' | 'description' | 'meta'> & {
            fields: Array<T>;
          }
        >;
      })
    | null,
):
  | {
      definition: Pick<DynamicSchemaDefinition, 'id'> & {
        sections: Array<
          Pick<DynamicSchemaDefinitionSection, 'id' | 'label' | 'description' | 'meta'> & {
            fields: Array<T>;
          }
        >;
      };
      values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
    }
  | undefined => {
  if (json == undefined || definition == undefined || definition == null) {
    return undefined;
  }
  const fields = definition.sections
    .flatMap((section) => section.fields)
    .map((field) => DynamicSchemaField.fromJSON(field));
  const values = new Map(
    fields.map((field) => {
      const value =
        field.type === DynamicSchemaDefinitionFieldType.SelectMultiple
          ? Immutable.Set(json[field.key])
          : json[field.key];
      return [field.key, field.getAdaptedValue(value)];
    }),
  );
  return { definition, values };
};

export const getBriefFileBucketIds = (
  project: Pick<ProjectType, 'creativeBriefDefinition'>,
  getBucketId: (key: DynamicSchemaFieldKey) => string,
) => {
  return new Map<DynamicSchemaFieldKey, string>(
    project.creativeBriefDefinition.sections
      .reduce<AnyDynamicSchemaField[]>((fields, section) => {
        return [...fields, ...section.fields.map((field) => DynamicSchemaField.fromJSON(field))];
      }, [])
      .filter((field) => field.type === 'FILES')
      .map((field) => {
        const bucketId = getBucketId(field.key);
        return [field.key, bucketId];
      }),
  );
};

export const getBriefFiles = async (
  project: Pick<ProjectType, 'id'>,
  fileMap: Map<DynamicSchemaFieldKey, string>,
  belfastClient: BelfastClient,
  filesWebApi: FilesWebApi,
) => {
  return new Map<DynamicSchemaFieldKey, FileReference[]>(
    await Promise.all(
      Array.from(fileMap).map(async ([fieldKey, bucketId]) => {
        const token = await filesWebApi.fetchToken({
          type: 'bucket',
          projectId: project.id,
          bucketType: 'creative_brief',
          bucketId: bucketId,
        });
        return belfastClient
          .getAllFilesInBucket(bucketId, token)
          .then((fileReferences) => [fieldKey, fileReferences] as [DynamicSchemaFieldKey, FileReference[]]);
      }),
    ),
  );
};

export const getAssetFiles = async (
  project: Pick<ProjectType, 'id' | 'bucketId' | 'assetCategoryDefinition' | 'assetCategoryValue'>,
  belfastClient: BelfastClient,
  filesWebApi: FilesWebApi,
) => {
  let assetFiles: Array<FileReference> = [];
  if (project.bucketId != null) {
    const token = await filesWebApi.fetchToken({ type: 'files', projectId: project.id });
    const { assetCategoryDefinition, assetCategoryValue } = project;
    if (assetCategoryValue != null && assetCategoryDefinition != null) {
      const fileIds = assetCategoryDefinition.sections.reduce<ReadonlyArray<string>>(
        (
          acc,
          cur: Pick<DynamicSchemaDefinitionSection, 'id' | 'meta' | 'label' | 'description'> & {
            fields: Array<
              | Pick<DynamicSchemaDefinitionFileIds, 'key' | 'type' | 'label'>
              | Pick<
                  DynamicSchemaDefinitionMultiLineText,
                  'key' | 'type' | 'label' | 'placeholder' | 'helperText' | 'isRequired'
                >
              | (Pick<DynamicSchemaDefinitionSelectOne, 'key' | 'type' | 'label' | 'helperText'> & {
                  choices: Array<Pick<DynamicSchemaDefinitionChoice, 'key' | 'label'>>;
                })
              | Pick<
                  DynamicSchemaDefinitionSingleLineText,
                  'key' | 'type' | 'label' | 'placeholder' | 'helperText' | 'isRequired'
                >
            >;
          },
        ) => {
          const fileIds = cur.fields
            .filter((field) => field.type == 'FILE_IDS')
            .flatMap((field) => assetCategoryValue.values.get(field.key)) as ReadonlyArray<string>;
          return [...acc, ...fileIds];
        },
        [],
      );
      for (const fileId of fileIds) {
        try {
          const file = await belfastClient.getFile(fileId, token);
          assetFiles = [...assetFiles, file];
        } catch (e) {
          // TOOD
          console.error(e);
        }
      }
    }
  }
  return assetFiles;
};
